import { memo, useMemo, useState } from 'react';
import { CircularProgress, Grid } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import moment from 'moment';

import DateSelectBox from './DateSelectBox';

import useAxiosPrivate from 'hooks/useAxiosPrivate';
import LineChart from 'components/ui/charts/LineChart';
import CurrencyFormat from 'components/ui/text-formats/CurrencyFormat';

const LineChartStatistics = () => {
  const axiosPrivate = useAxiosPrivate();
  const [searchParams] = useSearchParams();
  const [startAndFinishDate, setStartAndFinishDate] = useState({
    startDate: '',
    finishDate: '',
  });

  const accountId = searchParams.get('accountId');

  const search = `${
    startAndFinishDate.startDate ? '?from=' + startAndFinishDate.startDate : ''
  }${
    startAndFinishDate.finishDate
      ? '&till=' + startAndFinishDate.finishDate
      : ''
  }`;

  const { isLoading, isFetching, isError, data } = useQuery({
    queryKey: [
      'analytic/account/statistic/{accountId}',
      startAndFinishDate,
      accountId,
    ],
    queryFn: async function () {
      const response = await axiosPrivate.get(
        `analytic/account/statistic/${accountId}${search}`
      );
      return response.data.data;
    },

    enabled: !!accountId,

    retry: false,
  });

  const labels = useMemo(() => {
    if (data?.chart?.length) {
      return data?.chart?.map((ch) =>
        moment(ch?.date, 'YYYY-MM-DD').format('DD.MM.YYYY')
      );
    }
    return [];
  }, [data]);

  const dataset = useMemo(() => {
    if (data?.chart?.length) {
      return [
        {
          label: 'Chiqim',
          data: data?.chart.map((ch) => ch?.expense),
          borderColor: 'rgb(255, 99, 132)',
          backgroundColor: 'rgba(255, 99, 132, 0.5)',
        },
        {
          label: 'Kirim',
          data: data?.chart.map((ch) => ch?.income),
          borderColor: 'rgb(53, 162, 235)',
          backgroundColor: 'rgba(53, 162, 235, 0.5)',
        },
      ];
    }
    return [];
  }, [data]);

  return (
    <div className='residential-complex-item income-by-residential-complex !shadow-md !base-border'>
      <div className='residential-complex-item-header'>
        <div className='residential-complex-item-title font-semibold text-lg income-by-residential-complex-title'>
          Umumiy kirim-chiqim ma'lumoti
        </div>
        <div className='w-[150px]'>
          <DateSelectBox
            setStartAndFinishDate={setStartAndFinishDate}
            disabled={isLoading || isFetching}
          />
        </div>
      </div>

      <Grid container spacing={2} columns={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
        <Grid item={true} lg={2.5} md={2.5} sm={12} xs={12}>
          <div className='shadow-md p-2 border rounded-md w-full h-full'>
            <div className='font-semibold text-center'>Umumiy ma'lumot</div>
            <div className='p-2 border shadow-md mt-2'>
              Kirim UZS:{' '}
              <CurrencyFormat
                value={data?.summary?.total_income_sum}
                suffix=''
                decimalScale={2}
              />
            </div>
            <div className='p-2 border shadow-md mt-2'>
              Kirim USD:{' '}
              <CurrencyFormat
                value={data?.summary?.total_income_val}
                suffix=''
                decimalScale={2}
              />
            </div>
            <div className='p-2 border shadow-md mt-2'>
              Chiqim UZS:{' '}
              <CurrencyFormat
                value={data?.summary?.total_expense_sum}
                suffix=''
                decimalScale={2}
              />
            </div>
            <div className='p-2 border shadow-md mt-2'>
              Chiqim USD:{' '}
              <CurrencyFormat
                value={data?.summary?.total_expense_val}
                suffix=''
                decimalScale={2}
              />
            </div>
          </div>
        </Grid>
        <Grid item={true} lg={9.5} md={9.5} sm={12} xs={12}>
          <div className='shadow-md p-2 border rounded-md w-full h-full'>
            <div className='font-semibold text-center'>
              Kassalar kirim-chiqim grafigi
            </div>
            <div className='income-by-residential-body'>
              {isError ? (
                <div className='no-data-found-wrapper h-full'>
                  <i className='bi bi-exclamation-octagon text-lg mr-1' />{' '}
                  Ma'lumot topilmadi!
                </div>
              ) : isLoading || isFetching ? (
                <div className='h-[260px] flex items-center justify-center'>
                  <CircularProgress size={30} color='inherit' />
                </div>
              ) : labels?.length ? (
                <div className='sale-chart !base-border !shadow-md'>
                  <div className='h-[300px]'>
                    <LineChart
                      chartLabels={labels}
                      chartDatasets={dataset}
                      optionPlugins={{
                        legend: {
                          display: true,
                          position: 'right',
                          align: 'center',
                          labels: {
                            usePointStyle: true,
                            pointStyle: 'rectRounded',
                          },
                        },
                        tooltip: {
                          usePointStyle: true,
                          yAlign: 'bottom',
                          callbacks: {
                            labelPointStyle: function () {
                              return {
                                pointStyle: 'rectRounded',
                                rotation: 0,
                              };
                            },
                            title: () => null,
                          },
                          position: 'cursor',
                        },
                      }}
                      options={{
                        interaction: {
                          mode: 'index',
                          intersect: false,
                        },
                      }}
                      plugins={[
                        {
                          id: 'crosshairLabel',
                          afterDatasetsDraw(chart, args, plugins) {
                            const { ctx } = chart;
                            ctx.strokeStyle = 'gray';
                            if (chart?.crosshair?.length) {
                              ctx.save();
                              ctx.beginPath();
                              chart.crosshair.forEach((line) => {
                                ctx.setLineDash([5, 5]);
                                ctx.moveTo(line.startX, line.startY);
                                ctx.lineTo(line.endX, line.endY);
                                ctx.stroke();
                              });
                            }
                            ctx.setLineDash([]);
                          },
                          afterEvent(chart, args) {
                            const {
                              chartArea: { left, right, top, bottom },
                            } = chart;
                            const xCoor = args.event.x;
                            const yCoor = args.event.y;
                            if (!args?.inChartArea && chart?.crosshair) {
                              chart.crosshair = undefined;
                              args.changed = true;
                            } else if (args?.inChartArea) {
                              chart.crosshair = [
                                {
                                  startX: left,
                                  startY: yCoor,
                                  endX: right,
                                  endY: yCoor,
                                },
                                {
                                  startX: xCoor,
                                  startY: top,
                                  endX: xCoor,
                                  endY: bottom,
                                },
                              ];
                              args.changed = true;
                            }
                          },
                        },
                      ]}
                    />
                  </div>
                </div>
              ) : (
                <div className='h-[260px] flex items-center justify-center'>
                  <span className='text-gray-400 flex items-center justify-center'>
                    <i className='bi bi-exclamation-octagon text-xl leading-4 mr-1' />{' '}
                    Ma'lumot topilmadi!
                  </span>
                </div>
              )}
            </div>
          </div>
        </Grid>
      </Grid>
    </div>
  );
};

export default memo(LineChartStatistics);
